/**
 * W01d-D3 — the private subtree keyed by the CONFIRMED identity generation
 * (the same shared authority fixed under D1, read through AppShell's
 * keying): a generation change REMOUNTS the private state tree, so a
 * relogin/permission-generation transition can never reuse stale
 * component-local state. Unauthenticated/verifying/transitioning/
 * unavailable render NO private subtree at all (the gate below).
 */

import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
  useNavigate,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/AppShell';
import { NotFoundView, ErrorBoundaryView } from '@/components/errors';
import { useAuth, resolveAuthStatus } from '@/lib/auth';
import { useGenerationKey } from '@/lib/generation-key';
import { apiConfig, isAuthenticating } from '@/lib/api-config';
import { appIdentityScope } from '@/lib/identity-scope';
import { useEffect, useRef, useSyncExternalStore } from 'react';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundView,
  errorComponent: ({ error, reset }) => <ErrorBoundaryView error={error} reset={reset} />,
});

/** Routes that don't require authentication */
const PUBLIC_PATHS = ['/login', '/accept-invite'];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.includes(path);
}

/**
 * APP-W06a gate — rendered INSTEAD of the outlet for non-authenticating
 * builds (public preview, or fail-closed configuration). Login/auth-request
 * surfaces never mount and no credentialed request is issued. The screen is
 * neutral: an explanation only, no active authentication link (E1 link
 * policy is held), no manufactured provider health.
 */
function NonAuthenticatingScreen({ configUnavailable }: { configUnavailable: boolean }) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-text mb-2">11to1</h1>
        {configUnavailable ? (
          <p className="text-sm text-muted">
            This deployment is missing its required public configuration. Sign-in is disabled
            until the environment provides its explicit API target and release approval.
          </p>
        ) : (
          <p className="text-sm text-muted">
            This is a public preview. Sign-in and account actions are not available here.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * APP-W01a bootstrap states. Neither screen carries private content, auth
 * data, or an authentication request surface — the private subtree simply
 * does not exist until the current server-confirmed identity allows it.
 */
function IdentityVerifyingScreen({ transitioning }: { transitioning: boolean }) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-text mb-2">11to1</h1>
        <p className="text-sm text-muted">
          {transitioning
            ? 'Updating your session…'
            : 'Verifying your session with the server…'}
        </p>
      </div>
    </div>
  );
}

/**
 * Identity verification could not complete (timeout, unreachable backend,
 * malformed response). This is NOT signed-out: no sign-out claim is made and
 * no login redirect runs. A bounded retry re-runs the identity probe.
 */
function IdentityUnavailableScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-text mb-2">11to1</h1>
        <p className="text-sm text-muted mb-4">
          The server could not be reached, so your session could not be verified. This is
          not a sign-out — nothing was opened or closed. Check the connection and try again.
        </p>
        <button type="button" onClick={onRetry} className="btn-primary px-4 py-2">
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * W01d-D4 — the public ROOT-level logout-revocation notice seam.
 *
 * The truthful W01b outcome (`revocation-confirmed` / `revocation-unconfirmed` /
 * `session-already-absent`) is stored OUTSIDE the private subtree, so the
 * message remains visible after the private content collapses at logout.
 * Lifecycle is bounded: the notice is cleared on the next CONFIRMED identity
 * (a new generation owns fresh state) and never carries sensitive data —
 * only one of the three truthful classifications, rendered as plain copy.
 * Readers subscribe to the shared confirmed-identity authority so the
 * notice can never be consumed in the wrong generation.
 */
const LOGOUT_NOTICE_KEY = '11to1:logoutOutcome';
const LOGOUT_NOTICE_EVENT = '11to1:logoutOutcome';

export type LogoutOutcomeNotice =
  | 'revocation-confirmed'
  | 'revocation-unconfirmed'
  | 'session-already-absent';

/** Publish the truthful logout outcome (account page seam). */
export function publishLogoutOutcome(state: LogoutOutcomeNotice): void {
  try {
    sessionStorage.setItem(LOGOUT_NOTICE_KEY, state);
  } catch { /* presentation surface — droppable */ }
  window.dispatchEvent(new Event(LOGOUT_NOTICE_EVENT));
}

function readLogoutOutcome(): LogoutOutcomeNotice | null {
  try {
    const raw = sessionStorage.getItem(LOGOUT_NOTICE_KEY);
    if (raw === 'revocation-confirmed' || raw === 'revocation-unconfirmed' || raw === 'session-already-absent') {
      return raw;
    }
    if (raw) {
      // Unrecognised content: bounded-lifecycle hygiene, drop it.
      sessionStorage.removeItem(LOGOUT_NOTICE_KEY);
    }
  } catch { /* no storage */ }
  return null;
}

function clearLogoutOutcome(): void {
  try {
    sessionStorage.removeItem(LOGOUT_NOTICE_KEY);
  } catch { /* nothing to clear */ }
}

function useLogoutOutcomeNotice(): LogoutOutcomeNotice | null {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener(LOGOUT_NOTICE_EVENT, onChange);
      const invalidate = appIdentityScope.onInvalidate((prev, next) => {
        if (prev.generation !== next.generation && next.userId !== null) {
          // A NEW confirmed identity owns fresh state: the old notice ends
          // its bounded lifecycle here.
          clearLogoutOutcome();
          onChange();
        }
      });
      return () => {
        window.removeEventListener(LOGOUT_NOTICE_EVENT, onChange);
        invalidate();
      };
    },
    readLogoutOutcome,
    () => null,
  );
}

function LogoutOutcomeNoticeView({ state }: { state: LogoutOutcomeNotice }) {
  const copy =
    state === 'revocation-confirmed'
      ? 'Signed out — the server confirmed the session ended.'
      : state === 'session-already-absent'
        ? 'Signed out — the server confirmed the session was already gone.'
        : 'Signed out here — but the server did not confirm the revoke, so this device may still hold a live session cookie.';
  return (
    <div className="fixed inset-x-0 top-0 z-[60] p-3" role="status">
      <div className="card mx-auto max-w-md text-center">
        <p data-testid="logout-outcome-root" className="text-sm text-muted">{copy}</p>
      </div>
    </div>
  );
}

function RootLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const auth = useAuth();
  // Explicit provider-supplied identity state; legacy fixture contexts
  // (built without a status) derive a conservative equivalent.
  const status = resolveAuthStatus(auth);
  const revalidateAuth = auth.revalidateAuth;

  // W01d-D3 (top-of-component, unconditional hook order): the private
  // subtree is keyed by the CONFIRMED identity generation (the shared
  // authority — no second generation model). A generation change
  // (relogin/permission re-confirmation) REMOUNTS the private tree; a
  // non-authenticated lifecycle state keys to the sentinel and never
  // exposes the private subtree.
  const generationKey = useGenerationKey(status === 'authenticated');

  // W01d-D4 (top-of-component, unconditional hook order): the truthful
  // logout outcome renders at the ROOT level, OUTSIDE every private/held
  // subtree, so the message stays visible after the private subtree
  // collapses at logout (and after the post-logout redirect to /login).
  // Bounded lifecycle handled by the notice subscription.
  const logoutOutcome = useLogoutOutcomeNotice();

  // APP-W06a: gate before any auth-request surface mounts. The resolver is a
  // compile-time-constant module value, so this never varies between renders.
  const nonAuthenticating = !isAuthenticating();
  const configUnavailable = apiConfig.status !== 'ok';

  // Route guard: redirect confirmed-unauthenticated users to /login. Runs
  // only for a confirmed unauthenticated state — unavailable is a different
  // state and must not be treated as signed out. Never runs for a
  // non-authenticating build — the neutral screen replaces the whole tree.
  useEffect(() => {
    if (nonAuthenticating) return;
    if (status === 'unauthenticated' && !isPublicPath(currentPath)) {
      navigate({ to: '/login' });
    }
  }, [nonAuthenticating, status, currentPath, navigate]);

  /**
   * APP-W01d — cross-tab identity metadata signal + foreground revalidation.
   *
   * `11to1:identitySignal` is a NON-credential epoch marker: each confirmed
   * identity commit writes the current generation. Another tab's write of a
   * DIFFERENT value proves this tab's generation is not authoritative →
   * an uncertain foreground state, revalidated through the existing
   * coordinator (revalidateAuth). No credential, token or trusted identity
   * is ever broadcast — the marker is an epoch, not a fact.
   */
  const signalKeyRef = useRef('11to1:identitySignal');
  const currentGeneration = () => appIdentityScope.current().generation;
  useEffect(() => {
    if (nonAuthenticating) return;
    const key = signalKeyRef.current;
    const write = () => {
      // Publish this tab's CURRENT generation. A timestamp disambiguates
      // same-generation races (two tabs with the identical identity are
      // consistent); any DIFFERENT epoch read back requires revalidation.
      try {
        localStorage.setItem(key, JSON.stringify({ generation: currentGeneration(), at: Date.now() }));
      } catch { /* non-fatal */ }
    };

    const checkOtherTab = (event: StorageEvent) => {
      if (event.key !== key || event.newValue == null) return;
      let otherGeneration: unknown = null;
      try {
        otherGeneration = (JSON.parse(event.newValue) as { generation?: unknown }).generation ?? null;
      } catch { return; }
      if (typeof otherGeneration !== 'number') return;
      if (otherGeneration !== currentGeneration()) {
        // Another tab changed the identity metadata: this tab's cache state
        // is UNCERTAIN for credential-dependent surfaces. Revalidate.
        void revalidateAuth();
      }
    };

    const onShow = (event: PageTransitionEvent) => {
      // pageshow fires for bfcache restores too. If the restored page's
      // persisted epoch disagrees with the live epoch, revalidate; if the
      // session state could not be confirmed, the coordinator resolves it.
      if (event.persisted) {
        const stored = (() => {
          try {
            const raw = localStorage.getItem(key);
            return raw ? (JSON.parse(raw) as { generation?: unknown }).generation : null;
          } catch { return null; }
        })();
        if (typeof stored === 'number' && stored !== currentGeneration()) {
          void revalidateAuth();
        } else {
          // Even a MATCHING epoch may be bfcache-stale; a bounded server
          // revalidation is the only certain foreground check.
          void revalidateAuth();
        }
      }
    };

    write();
    appIdentityScope.onInvalidate(write);
    window.addEventListener('storage', checkOtherTab);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('storage', checkOtherTab);
      window.removeEventListener('pageshow', onShow);
    };
  }, [nonAuthenticating, revalidateAuth]);

  if (nonAuthenticating) {
    return <NonAuthenticatingScreen configUnavailable={configUnavailable} />;
  }

  // W01d-D4: the truthful logout outcome renders at the ROOT level, OUTSIDE
  // every private/held subtree, so the message stays visible after the
  // private subtree collapses at logout (and after the post-logout redirect
  // to /login). Bounded lifecycle handled by the notice subscription
  // (hook order established at the top of the component).
  const logoutNotice = logoutOutcome ? <LogoutOutcomeNoticeView state={logoutOutcome} /> : null;

  // Login pages have their own full-screen layout
  const isEntryPage =
    currentPath === '/' || currentPath === '/login' || currentPath === '/accept-invite';

  // APP-W01a: the private subtree is gated BEFORE any render, not merely
  // redirected after render. Nothing private mounts, and no private data
  // read starts, until the current server-confirmed identity exists.
  if (!isEntryPage && status !== 'authenticated') {
    if (status === 'unauthenticated') {
      // Confirmed no-session on a private path: nothing private renders; the
      // redirect effect above navigates to /login. Neutral blank frame + the
      // root logout notice (D4: the outcome must remain visible here).
      return logoutNotice;
    }
    if (status === 'unavailable') {
      return (
        <>
          {logoutNotice}
          <IdentityUnavailableScreen
            onRetry={() => { void revalidateAuth(); }}
          />
        </>
      );
    }
    // verifying / transitioning: hold, never render private content.
    return (
      <>
        {logoutNotice}
        <IdentityVerifyingScreen transitioning={status === 'transitioning'} />
      </>
    );
  }

  // Login pages have their own full-screen layout
  if (isEntryPage) {
    return (
      <>
        {logoutNotice}
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      </>
    );
  }

  // W01d-D3: keying established at the top of the component (hook order).
  return (
    <>
      {logoutNotice}
      <AppShell key={generationKey}>
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      </AppShell>
    </>
  );
}
